import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  addMapMarkers,
  clearMapMarkers,
  initializeMap,
  loadMapbox
} from "../../../../Mapbox/mapbox";
import { Svg } from "../../../../Shared/Assets/Svg/React";
import { initializePopovers, removePopover } from "../../../../Shared/Popover/popover";
import { notEmpty } from "../../../../Shared/Utilities/filters";
import { useProvetContext } from "../../context";
import { ProvetDepartment } from "../../types";
import { DepartmentMarker } from "./provetDepartmentMarker";

interface DepartmentMapProps {
  id: string;
  visible: boolean;
  departments: ProvetDepartment[];
  oldBookingDomain?: string;
  onSelectDepartment: (department: ProvetDepartment) => void;
  defaultLatitude: number;
  defaultLongitude: number;
  defaultZoom: number;
  mapboxAccessToken?: string;
}

export const DepartmentMap = ({
  id,
  visible,
  departments,
  oldBookingDomain,
  defaultLatitude,
  defaultLongitude,
  defaultZoom = 4,
  mapboxAccessToken,
  onSelectDepartment
}: DepartmentMapProps) => {
  const provetContext = useProvetContext();
  const [mapboxLoaded, setMapboxLoaded] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  const mapRef = useRef(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverIdsRef = useRef<string[]>([]);
  const selectedMarkerRef = useRef<{ marker: HTMLElement; popover: HTMLElement } | null>(
    null
  );

  // Aligns the popover anchor with the currently selected marker, and repositions its popover.
  const syncAnchorToSelectedMarker = useCallback(() => {
    const anchor = anchorRef.current;
    const selected = selectedMarkerRef.current;

    if (!anchor || !selected) {
      return;
    }

    const markerRect = selected.marker.getBoundingClientRect();
    const containerRect = anchor.offsetParent?.getBoundingClientRect();

    if (!containerRect) {
      return;
    }

    anchor.style.left = `${markerRect.left - containerRect.left}px`;
    anchor.style.top = `${markerRect.top - containerRect.top}px`;
    anchor.style.width = `${markerRect.width}px`;
    anchor.style.height = `${markerRect.height}px`;

    const popoverRecord = window.ani_getPopover?.(selected.popover);

    if (popoverRecord?.isOpen()) {
      popoverRecord.reposition();
    }
  }, []);

  useEffect(() => {
    if (!mapboxLoaded) {
      loadMapbox(mapboxAccessToken ?? "");
      setMapboxLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapboxLoaded]);

  useEffect(() => {
    if (visible && !mapInitialized && mapRef.current) {
      const initMap = () => {
        const map = initializeMap(
          mapRef.current!,
          id,
          defaultLatitude,
          defaultLongitude,
          defaultZoom
        );

        // Keep the anchor (and open popover) attached to the marker while the map moves
        map.on("move", syncAnchorToSelectedMarker);

        map.on("load", () => {
          if (!!mapRef.current) {
            initializePopovers(mapRef.current as Element);
          }
        });

        setMapInitialized(true);
      };

      initMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapInitialized, visible]);

  const updateDepartmentMarkers = useCallback(() => {
    if (mapInitialized) {
      // Initialized popovers are moved to document.body, so they can't be found by
      // searching within the map. If they aren't removed, the new popovers (which reuse
      // the same ids) are considered already initialized and never get any behavior.
      popoverIdsRef.current.forEach(popoverId => {
        const popover = document.getElementById(popoverId);

        if (popover) {
          removePopover(popover);
        }
      });

      popoverIdsRef.current = [];
      selectedMarkerRef.current = null;

      // Clear existing map markers before adding new ones. Otherwise the selector
      // ends up using the old markers.
      clearMapMarkers(id);

      const markers = departments
        .map(department => {
          const markerId = `department-marker-${department.id}`;
          const sourceMarker = document.getElementById(markerId);
          const markerElement = sourceMarker?.cloneNode(true) as HTMLElement | undefined;

          if (markerElement) {
            markerElement.id = `${markerId}-map`;

            const popover = markerElement.querySelector<HTMLElement>(
              `[id="department-popover-${department.id}"]`
            );
            const confirmButton = markerElement.querySelector<HTMLButtonElement>(
              '[data-online-booking-action="confirmClinicFromMapMarker"]'
            );

            if (popover) {
              const popoverId = `${popover.id}-map`;
              popover.id = popoverId;
              popoverIdsRef.current.push(popoverId);
              markerElement.setAttribute("aria-controls", popoverId);

              const closeButton = popover.querySelector<HTMLElement>(
                `[id="department-popover-${department.id}-close-button"]`
              );
              if (closeButton) {
                closeButton.id = `${popoverId}-close-button`;
              }

              // Registered before the popover's own open handlers (added in initializePopovers),
              // so the anchor is in place by the time the popover positions itself.
              const selectMarker = () => {
                selectedMarkerRef.current = { marker: markerElement, popover };
                syncAnchorToSelectedMarker();
              };

              markerElement.addEventListener("click", selectMarker);
              markerElement.addEventListener("keydown", e => {
                if (e.key === "Enter") {
                  selectMarker();
                }
              });
            }

            confirmButton?.addEventListener("click", () => {
              onSelectDepartment(department);

              if (popover && window.ani_getPopover) {
                window.ani_getPopover(popover)?.close();
              }
            });
          }

          const { Latitude, Longitude } = department.location ?? {};

          if (
            Latitude !== undefined &&
            Latitude !== 0 &&
            Longitude !== undefined &&
            Longitude !== 0 &&
            markerElement
          ) {
            return {
              element: markerElement,
              position: {
                lat: Latitude,
                lng: Longitude
              },
              title: department.name
            };
          }
        })
        .filter(notEmpty);

      addMapMarkers(id, markers, true);

      if (mapRef.current) {
        initializePopovers(mapRef.current as Element);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departments, mapInitialized]);

  useEffect(() => {
    updateDepartmentMarkers();
  }, [departments, mapInitialized]);

  return (
    <div>
      <div
        data-access-token={mapboxAccessToken}
        data-default-latitude={defaultLatitude}
        data-default-longitude={defaultLongitude}
        data-default-zoom={defaultZoom}
        data-provet-department-finder-map
        data-language={provetContext.language}
        id={id}
      >
        <div className="w-full">
          <div className="relative">
            <div
              className={classNames(
                "w-full h-[53.125rem] md:h-[42.5rem] rounded-sm",
                "border-y border-turquoise-400"
              )}
              ref={mapRef}
              data-provet-department-finder-map-container
            ></div>
            <div
              className="absolute invisible pointer-events-none"
              id={`${id}-popover-anchor`}
              ref={anchorRef}
            ></div>
          </div>
        </div>

        <div className="hidden">
          <div
            className="flex w-2xl h-2xl justify-center items-center bg-yellow-800 rounded-full text-dark-blue shadow-md"
            id={"user-position-marker"}
          >
            <Svg
              href="/dist/sprite.svg#sys-location"
              additionalClass={"text-dark-blue"}
            />
          </div>
        </div>
      </div>
      <div className="hidden markers">
        {departments.map((department, index) => (
          <DepartmentMarker
            key={index}
            department={department}
            oldBookingDomain={oldBookingDomain}
            onSelectDepartment={onSelectDepartment}
          />
        ))}
      </div>
    </div>
  );
};
